import { Formik, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, TouchableOpacity } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { Spinner } from '@components/shared/Spinner';
import VerificationCodeInput from '@screens/Auth/VerificationCodeInput.web';
import theme from '@themes/theme';
import {
  checkEmailVerificationCode,
  EMAIL_VERIFICATION_CODE_LENGTH,
  sendEmailVerificationCode,
} from '@utils/emailVerification';

const RESEND_SECONDS = 30;

export type InviteEmailVerificationStepProps = {
  email: string;
  /**
   * Registers with the entered code. Resolves to a message to show on this
   * step (e.g. an invalid/expired code), or `null` when the caller has moved
   * on — success, or an error it surfaces back on the form.
   */
  onSubmitCode: (code: string) => Promise<string | null>;
  onBack: () => void;
};

type CodeValues = { verify_code: string };

/**
 * TA-11025: the email OTP step for the v3 invite sign-up forms
 * (ScopedSignupModal.web, InvitationPreview.web). The caller has already sent
 * the first code before showing this step, so a send failure lands on the
 * form the user just filled in rather than on an empty code screen.
 *
 * Same copy and resend rhythm as RegisterVerifyEmail.web, but prop-driven:
 * that screen is bound to RegisterForm's Formik values and stack navigation,
 * neither of which exists inside the invite modal.
 */
const StepBody: React.FC<InviteEmailVerificationStepProps> = ({
  email,
  onSubmitCode,
  onBack,
}) => {
  const { t } = useTranslation('models');
  const { values, setFieldValue } = useFormikContext<CodeValues>();
  const [countdown, setCountdown] = useState(RESEND_SECONDS);
  const [isSending, setIsSending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only a rejected code blocks Verify until the code is edited; after a
  // network or rate-limit error the same, still-valid code can be retried.
  const [isIncorrectCode, setIsIncorrectCode] = useState(false);
  const [hasResent, setHasResent] = useState(false);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const code = values.verify_code ?? '';
  const isCodeComplete = code.length === EMAIL_VERIFICATION_CODE_LENGTH;
  const isBusy = isSending || isSubmitting;
  const isResendDisabled = isBusy || countdown > 0;

  const resend = async () => {
    setIsSending(true);
    setError(null);
    // The rejected code is cleared below, so its block on Verify goes with it.
    setIsIncorrectCode(false);
    setHasResent(false);
    setFieldValue('verify_code', '');
    try {
      const result = await sendEmailVerificationCode(email);
      if (!result.ok) {
        setError(
          result.error ?? t('registerVerifyEmail.unableToSendVerificationCode')
        );
        return;
      }
      setCountdown(RESEND_SECONDS);
      setHasResent(true);
    } catch {
      setError(t('registerVerifyEmail.networkError'));
    } finally {
      setIsSending(false);
    }
  };

  const submit = async () => {
    if (!isCodeComplete || isBusy) return;
    setIsSubmitting(true);
    setError(null);
    setIsIncorrectCode(false);
    setHasResent(false);
    try {
      // Interim guard until TA-11024 (tasktag-backend #4495) is live: today
      // v3 users.json drops `email_verification_code` unchecked, so a wrong
      // code would still register. Pre-check it here so it stays on this
      // step. Safe to keep afterwards: check does not consume the code, and
      // users.json re-verifies it.
      const check = await checkEmailVerificationCode(email, code);
      // A rejection without a message reads as an incorrect code, as in
      // RegisterVerifyEmail.
      const message = check.ok ? await onSubmitCode(code) : (check.error ?? '');
      if (message !== null) {
        const incorrect = !message || message.startsWith('Invalid');
        setIsIncorrectCode(incorrect);
        setError(incorrect ? t('registerVerifyEmail.incorrectCode') : message);
      }
    } catch {
      setError(t('registerVerifyEmail.networkError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box width='100%' alignItems='center'>
      <Box flexDirection='row' alignItems='center' width='100%' mb='l'>
        <Pressable
          onPress={onBack}
          disabled={isBusy}
          hitSlop={8}
          accessibilityRole='button'
          accessibilityLabel={t('shared:back')}
          testID='invite-verify-email-back'>
          <Icon name='ChevronLeft' variant='s' color='textPrimary' />
        </Pressable>
        <Box flex={1} alignItems='center'>
          <Text variant='heading2' textAlign='center'>
            {t('registerVerifyEmail.title')}
          </Text>
        </Box>
        <Box width={24} />
      </Box>

      <Text
        variant='webBodySecondary'
        color='textSecondary'
        textAlign='center'
        mb='m'>
        {t('registerVerifyEmail.subtitle')}
      </Text>
      <Box
        backgroundColor='grey01'
        borderRadius='xs'
        paddingHorizontal='s'
        paddingVertical='xs'
        mb='l'>
        <Text variant='labelSmall' color='textSecondary'>
          {email}
        </Text>
      </Box>

      <Box minHeight={64} mb='m' width='100%' alignItems='center'>
        <VerificationCodeInput
          inputSize={EMAIL_VERIFICATION_CODE_LENGTH}
          hasError={!!error}
          forceSuccessState={hasResent}
          disabled={isBusy}
          onTextChanged={(text) => {
            setFieldValue('verify_code', text);
            if (error || hasResent) {
              setError(null);
              setIsIncorrectCode(false);
              setHasResent(false);
            }
          }}
        />
        {!!error && (
          <Text
            variant='bodySecondary'
            color='alertRed'
            marginTop='s'
            textAlign='center'>
            {error}
          </Text>
        )}
      </Box>

      <Box flexDirection='row' alignItems='center'>
        <TouchableOpacity
          testID='invite-resend-email-code'
          disabled={isResendDisabled}
          onPress={resend}>
          <Text
            variant='labelSmallBold'
            color={isResendDisabled ? 'grey04' : 'greenSecondary'}>
            {countdown > 0
              ? t('registerVerifyEmail.resendCodeIn', {
                  time: `00:${countdown.toString().padStart(2, '0')}`,
                })
              : t('registerVerifyEmail.resendCode')}
          </Text>
        </TouchableOpacity>
        {isSending && (
          <Box marginLeft='xs'>
            <Spinner
              size={14}
              strokeWidth={2}
              color={theme.colors.greenSecondary}
            />
          </Box>
        )}
      </Box>

      <Box width='100%' mt='xl'>
        <Button
          variant='primary'
          fullWidth
          isSmall
          disabled={!isCodeComplete || isBusy || isIncorrectCode}
          isLoading={isSubmitting}
          onPress={submit}
          accessibilityLabel={t('registerVerifyEmail.verifyEmailButton')}>
          {t('registerVerifyEmail.verifyEmailButton')}
        </Button>
      </Box>
    </Box>
  );
};

export const InviteEmailVerificationStep: React.FC<
  InviteEmailVerificationStepProps
> = (props) => (
  // VerificationCodeInput reads `verify_code` from a Formik context to know
  // when to clear itself, so the step carries its own tiny form.
  <Formik<CodeValues> initialValues={{ verify_code: '' }} onSubmit={() => {}}>
    <StepBody {...props} />
  </Formik>
);
